import { useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';
import { formatDistance } from 'date-fns';

import {
  Grid,
  Text,
  useMount,
  TabList,
  TabPanel,
  Tab,
} from '@simplywallst/ui-core';
import { LinkOutline } from '@simplywallst/ui-core/icons';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';
import BrazeContentCard from '@/components/BrazeContentCard';

import type { Portfolio } from '@/hooks/usePortfolio';

import { useIsMobileScreen } from '@/hooks/useMedia';

import PortfolioMoreOptions from '../PortfolioMoreOptions';
import { PortfolioPageHeader } from '../PortfolioPageHeader';
import { HoldingsPanel } from '../HoldingsPanel';
import { AnalysisPanel } from '../AnalysisPanel';

import { StyledHr, StyledGrid, BrazeContentCardWrapper } from './styled';

type PortfolioTab = 'holdings' | 'analysis';

type Props = {
  portfolio: Portfolio;
};

const PopulatedState = ({ portfolio }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const [selectedPanel, setSelectedPanel] = useState<PortfolioTab>('holdings');

  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: 'populatedState',
  });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  const timeSinceLastUpdate = useMemo(() => {
    return formatDistance(new Date(portfolio.updatedAt), new Date(), {
      addSuffix: true,
    });
  }, [portfolio.updatedAt]);

  return (
    <Track>
      <MiniPriceChartDefinitions />
      <BrazeContentCardWrapper>
        <BrazeContentCard feedType="new-portfolio.top" anchorTarget="_blank" />
      </BrazeContentCardWrapper>
      <StyledGrid gap="8px">
        <PortfolioPageHeader portfolio={portfolio}>
          {isMobileScreen ? (
            <PortfolioMoreOptions portfolio={portfolio} />
          ) : null}
          <Grid
            templateRows="auto"
            templateColumns="minmax(min-content, max-content) auto"
            templateColumnsMd="auto max-content min-content"
            columnGap="20px"
            alignItems="center"
          >
            <Text typography="tiny" color="whiteRGBA" opacity={0.3}>
              Updated{' '}
              <Text as="span" color="whiteRGBA" opacity={0.5}>
                {timeSinceLastUpdate}
              </Text>
            </Text>
            {portfolio.type === 'linked' && (
              <Grid templateColumns="min-content auto" alignItems="center">
                <LinkOutline
                  fill="white"
                  opacity={0.3}
                  width="24px"
                  height="24px"
                />
                <Text typography="tiny" color="whiteRGBA" opacity={0.3}>
                  Linked with {portfolio.link.brokerageName}
                </Text>
              </Grid>
            )}
            {!isMobileScreen ? (
              <PortfolioMoreOptions portfolio={portfolio} />
            ) : null}
          </Grid>
        </PortfolioPageHeader>
        <Grid>
          <TabList templateColumns="max-content max-content">
            <Tab<PortfolioTab>
              value="holdings"
              selected={selectedPanel === 'holdings'}
              onClick={() => setSelectedPanel('holdings')}
              styleType="subnav"
            >
              Holdings
            </Tab>
            <Tab<PortfolioTab>
              value="analysis"
              selected={selectedPanel === 'analysis'}
              onClick={() => setSelectedPanel('analysis')}
              styleType="subnav"
            >
              Analysis
            </Tab>
          </TabList>
          <StyledHr />
        </Grid>
      </StyledGrid>
      <TabPanel<PortfolioTab>
        value="holdings"
        selected={selectedPanel === 'holdings'}
      >
        <HoldingsPanel portfolio={portfolio} />
      </TabPanel>
      <TabPanel<PortfolioTab>
        value="analysis"
        selected={selectedPanel === 'analysis'}
      >
        <AnalysisPanel portfolio={portfolio} />
      </TabPanel>
    </Track>
  );
};

export default PopulatedState;
